import useSession from '@/hooks/useSession';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

const UPDATE_DISMISS_COUNTER_KEY = 'banner-dismiss-counter';
const CURRENT_BANNER_ID: string | undefined = 'deep-dive-hdx-2026';
// The banner stops showing once it has been dismissed this many times
const MAX_DISMISSALS = 2;

const storeBannerDismissCounter = (count: number) => {
  try {
    localStorage.setItem(
      UPDATE_DISMISS_COUNTER_KEY,
      JSON.stringify({ id: CURRENT_BANNER_ID, count })
    );
  } catch {
    // Storage can be blocked; the banner then comes back on the next visit
  }
};

export const useBanner = (): {
  showBanner: boolean;
  onBannerDismiss: () => void;
  onBannerComplete: () => void;
} => {
  const [showBanner, setShowBanner] = useState(false);
  const { pushUserEvent } = useSession();

  const onBannerDismiss = () => {
    setShowBanner(false);
    const newValue = getBannerDismisssCounter() + 1;
    pushUserEvent(`dismiss_banner_${CURRENT_BANNER_ID}_${newValue}`);
    storeBannerDismissCounter(newValue);
  };

  // Acting on the banner retires it, as if it had been dismissed for good
  const onBannerComplete = () => {
    setShowBanner(false);
    storeBannerDismissCounter(MAX_DISMISSALS);
  };

  const getBannerDismisssCounter = (): number => {
    try {
      const data = JSON.parse(
        localStorage.getItem(UPDATE_DISMISS_COUNTER_KEY) || '0'
      );
      if (data.id !== CURRENT_BANNER_ID) {
        storeBannerDismissCounter(0);
        return 0;
      }
      return data.count || 0;
    } catch (error) {
      storeBannerDismissCounter(0);
    }
    return 0;
  };

  useQuery({
    queryKey: ['app-banner', CURRENT_BANNER_ID],
    queryFn: async () => {
      if (CURRENT_BANNER_ID === undefined) {
        return null;
      }
      const dismissCounter = getBannerDismisssCounter();

      if (dismissCounter < MAX_DISMISSALS) {
        setShowBanner(true);
      }

      return null;
    },
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    staleTime: Infinity,
  });

  return { showBanner, onBannerDismiss, onBannerComplete };
};
